/**
 * gateway/v1 — contract between Central and external gateways (docs/architecture/04-gateway-contract.md).
 *
 * Transport: WebSocket (WSS outside localhost) opened by the gateway to /api/gateway/v1/link,
 * one JSON envelope per text frame. Delivery is at-least-once in both directions:
 *  - every sequenced message has `seq`, monotonic per direction within one gateway boot (`boot_id`),
 *    starting at 1 and continuing across link reconnects (it restarts only when boot_id changes),
 *    and a globally unique `id` used for duplicate detection. Gaps are allowed (a gateway may drop
 *    low-priority messages under overflow) and are counted, never treated as fatal;
 *  - the receiver acknowledges cumulatively with an `ack` message; `ack`, `link.error` and the per-link
 *    handshake (`gateway.hello`, `gateway.welcome`) are unsequenced (seq = 0) and never acknowledged;
 *  - backpressure: a sender must not have more than the peer's `max_unacked` messages outstanding;
 *  - after reconnect, `gateway.welcome.resume_from_seq` tells the gateway what Central already has,
 *    and `gateway.hello.resume.last_central_seq` tells Central what the gateway already has.
 */
import { z } from 'zod';
import { CapabilityKey, ConnRef, DeviceRef, IsoTimestamp, JsonObject, MetricKey, Uuid, } from '../../common.js';
export const GATEWAY_CONTRACT = 'gateway/v1';
export const GATEWAY_LINK_PATH = '/api/gateway/v1/link';
// ---------------------------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------------------------
export const Envelope = z.object({
    v: z.literal(1),
    type: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z][a-z0-9_.]*$/),
    id: Uuid,
    seq: z.number().int().nonnegative(),
    ts: IsoTimestamp,
    corr: z.string().max(128).nullable(),
    body: JsonObject,
});
// ---------------------------------------------------------------------------------------------
// Shared bodies
// ---------------------------------------------------------------------------------------------
export const AckBody = z.object({ up_to_seq: z.number().int().nonnegative() });
export const DisconnectReason = z.enum([
    'peer_closed',
    'idle_timeout',
    'detect_timeout',
    'protocol_error',
    'superseded',
    'gateway_shutdown',
    'resource_limit',
    'unknown',
]);
export const CommandStatus = z.enum([
    'ACCEPTED',
    'SENT',
    'ACKNOWLEDGED',
    'SUCCESS',
    'FAILED',
    'TIMEOUT',
    'REJECTED',
    'CANCELLED',
]);
/**
 * Evidence about a device reached *through* the device of a connection (e.g. a radio node behind a concentrator).
 * Additive in gateway/v1 (minor): reachable = the device was heard, not_answering = it failed to answer while the
 * upstream device answered, unknown = no current evidence. Silence alone is never not_answering.
 */
export const DownstreamState = z.enum(['reachable', 'not_answering', 'unknown']);
const DownstreamEntry = z.object({
    device_ref: DeviceRef,
    state: DownstreamState,
    evidence: z.string().max(32).optional(),
    at: IsoTimestamp,
    detail: JsonObject.optional(),
});
const ConnectionEntry = z.object({
    conn_ref: ConnRef,
    identifiers: z.array(DeviceRef).default([]),
    protocol: z.string().max(64).nullable(),
    since: IsoTimestamp,
    remote_addr: z.string().max(128).optional(),
    /** devices currently known behind this connection's device, with their latest evidence (additive) */
    downstream: z.array(DownstreamEntry).max(4096).optional(),
    /** listener port the connection arrived on (additive) */
    local_port: z.number().int().min(1).max(65535).optional(),
    /** cumulative session counters, same keys as `device.disconnected.counters` (additive) */
    counters: z.record(z.string(), z.number().int().nonnegative()).optional(),
});
/** Session traffic counters: TCP payload bytes and recv/send calls since accept(), plus the INFERRED wire estimate. */
const TrafficCounters = z.record(z.string(), z.number().int().nonnegative());
/**
 * One open connection in a `gateway.traffic_counters` report (architecture/14 §3). `counters` are cumulative since
 * accept() — Central accounts traffic from them (monotonic per connection), `delta` is informative. `device_ref` is the
 * connection's own identity: the device holding the SIM (a concentrator, never a node reached through it).
 */
const TrafficEntry = z.object({
    conn_ref: ConnRef,
    protocol: z.string().max(64).nullable(),
    since: IsoTimestamp,
    local_port: z.number().int().min(1).max(65535).optional(),
    device_ref: DeviceRef.optional(),
    identified_at: IsoTimestamp.optional(),
    last_rx_at: IsoTimestamp.optional(),
    last_tx_at: IsoTimestamp.optional(),
    counters: TrafficCounters,
    delta: TrafficCounters.optional(),
});
/**
 * Cellular registration and login facts of one device connection, as the device's login frame carried them
 * (additive in `device.identified`, 0.34.0; architecture/04 §4). Central stores it unchanged on the
 * `device_connections` row for the per-device connection history; nothing is derived from it at ingest.
 *  - `raw`: the login parameters the adapter did not interpret, by vendor key (≤ 40 keys, values as text);
 *  - `network`: the serving cell at login — every field optional, only what the protocol's login carries
 *    (which fields each protocol provides: docs/protocols/*) — never inferred;
 *  - `access_time_s`: the device-reported registration/access time, when the login carries one.
 */
export const DeviceLoginNetwork = z.object({
    rat: z.enum(['LTE', 'GSM', 'NONE']).optional(),
    mcc: z.string().max(3).optional(),
    mnc: z.string().max(3).optional(),
    plmn: z.string().max(7).optional(),
    cell_id: z.string().max(16).optional(),
    tac: z.string().max(8).optional(),
    lac: z.string().max(8).optional(),
    pci: z.number().int().optional(),
    earfcn: z.number().int().optional(),
    band: z.string().max(8).optional(),
    arfcn: z.number().int().optional(),
    rsrp_dbm: z.number().int().optional(),
    rsrq_db: z.number().int().optional(),
    rssi_dbm: z.number().int().optional(),
    sinr_db: z.number().int().optional(),
    rxlev_dbm: z.number().int().optional(),
    state: z.string().max(16).optional(),
    signal: z.number().int().optional(),
    imsi: z.string().max(20).optional(),
    raw_servingcell: z.string().max(200).optional(),
});
export const DeviceLogin = z.object({
    raw: z
        .record(z.string().max(32), z.string().max(200))
        .refine((r) => Object.keys(r).length <= 40, { message: 'at most 40 raw login keys' })
        .optional(),
    network: DeviceLoginNetwork.optional(),
    access_time_s: z.number().int().nonnegative().optional(),
});
// ---------------------------------------------------------------------------------------------
// Gateway → Central
// ---------------------------------------------------------------------------------------------
export const GatewayToCentral = {
    ack: AckBody,
    'gateway.hello': z.object({
        gateway_key: z.string().min(1).max(64),
        boot_id: Uuid,
        software_version: z.string().max(64),
        contracts: z.array(z.string()).min(1),
        protocols: z.array(z.string().max(64)).default([]),
        listeners: z
            .array(z.object({ port: z.number().int().min(1).max(65535), protocols: z.array(z.string()) }))
            .default([]),
        max_unacked: z.number().int().min(1).max(100_000),
        resume: z.object({ last_central_seq: z.number().int().nonnegative() }).nullable(),
    }),
    'gateway.heartbeat': z.object({
        uptime_s: z.number().nonnegative(),
        connected_devices: z.number().int().nonnegative(),
        outbox_depth: z.number().int().nonnegative(),
    }),
    'gateway.health': z.object({
        metrics: z.record(z.string(), z.number()),
    }),
    'gateway.connections.snapshot': z.object({
        snapshot_id: z.string().max(64),
        connections: z.array(ConnectionEntry),
    }),
    'device.connected': z.object({
        conn_ref: ConnRef,
        at: IsoTimestamp,
        remote_addr: z.string().max(128).optional(),
        local_port: z.number().int().min(1).max(65535).optional(),
    }),
    'device.identified': z.object({
        conn_ref: ConnRef,
        at: IsoTimestamp,
        protocol: z.string().min(1).max(64),
        identifiers: z.array(DeviceRef).min(1),
        firmware: z.string().max(128).optional(),
        model_hint: z.string().max(128).optional(),
        capabilities_hint: z.array(CapabilityKey).optional(),
        /** login facts of this connection (cellular registration, raw login parameters; additive) */
        login: DeviceLogin.optional(),
    }),
    'device.disconnected': z.object({
        conn_ref: ConnRef,
        at: IsoTimestamp,
        reason: DisconnectReason,
        counters: z.record(z.string(), z.number().int().nonnegative()).optional(),
    }),
    /**
     * The gateway's function catalog with its current policy, after every welcome (additive). `protocol` + `scope`
     * address the devices a row applies to: "direct" = the device on a connection of that protocol, "downstream" = a
     * device reached through it. States follow the capability model: vendor support, implemented, tested offline,
     * tested live, operationally enabled.
     */
    'gateway.capabilities': z.object({
        catalog_version: z.string().max(64),
        functions: z
            .array(z.object({
            protocol: z.string().min(1).max(64),
            scope: z.enum(['direct', 'downstream']),
            function: z.string().max(512),
            capability: z.string().max(96).optional(),
            config_key: z.string().max(96).optional(),
            kind: z.string().max(32),
            safety: z.string().max(32),
            supported_by_vendor: z.string().max(32),
            vendor_ref: z.string().max(256).optional(),
            implemented: z.boolean(),
            tested_offline: z.boolean(),
            tested_live: z.boolean(),
            operationally_enabled: z.boolean(),
            allowed_devices: z.array(z.string().max(128)).max(1000).optional(),
            note: z.string().max(1024).optional(),
        }))
            .max(5000),
    }),
    /** periodic per-connection traffic counters (≤ 1000 entries per message; idle connections skipped) */
    'gateway.traffic_counters': z.object({
        at: IsoTimestamp,
        interval_s: z.number().int().nonnegative().optional(),
        estimate: z
            .object({ header_bytes: z.number().int().min(1).max(1000), method: z.string().max(256) })
            .optional(),
        entries: z.array(TrafficEntry).max(1000),
    }),
    'device.reachability': z.object({
        conn_ref: ConnRef,
        entries: z.array(DownstreamEntry).min(1).max(1000),
    }),
    'device.telemetry': z.object({
        conn_ref: ConnRef,
        device_ref: DeviceRef,
        observed_at: IsoTimestamp,
        device_clock: z.string().max(64).optional(),
        channel: z.number().int().nonnegative().optional(),
        metrics: z.record(MetricKey, z.number()),
        vendor: JsonObject.optional(),
        raw_ref: z.string().max(128).optional(),
    }),
    'device.state': z.object({
        conn_ref: ConnRef,
        device_ref: DeviceRef,
        observed_at: IsoTimestamp,
        channel: z.number().int().nonnegative().optional(),
        reported: JsonObject,
    }),
    'device.event': z.object({
        conn_ref: ConnRef,
        device_ref: DeviceRef,
        observed_at: IsoTimestamp,
        event_key: z.string().min(1).max(96),
        severity_hint: z.enum(['info', 'warning', 'critical']).optional(),
        data: JsonObject.optional(),
    }),
    'device.raw_frame': z.object({
        conn_ref: ConnRef,
        device_ref: DeviceRef.optional(),
        direction: z.enum(['RX', 'TX']),
        at: IsoTimestamp,
        hex: z
            .string()
            .max(131_072)
            .regex(/^([0-9A-F]{2})*$/),
        protocol: z.string().max(64).optional(),
        parsed: JsonObject.optional(),
        parser_version: z.string().max(32).optional(),
        error: z.string().max(512).optional(),
    }),
    'command.update': z.object({
        command_id: Uuid,
        status: CommandStatus,
        at: IsoTimestamp,
        detail: JsonObject.optional(),
        error_code: z.string().max(64).optional(),
        raw_refs: z.array(z.string().max(128)).optional(),
    }),
    'device.query_result': z.object({
        query_id: Uuid,
        device_ref: DeviceRef,
        ok: z.boolean(),
        result: JsonObject.optional(),
        error_code: z.string().max(64).optional(),
    }),
};
// ---------------------------------------------------------------------------------------------
// Central → Gateway
// ---------------------------------------------------------------------------------------------
export const GatewayConfig = z.object({
    heartbeat_interval_s: z.number().int().min(1).max(3600),
    raw_capture: z.enum(['off', 'errors', 'sampled', 'all']).default('errors'),
    log_level: z.enum(['error', 'warn', 'info', 'debug']).default('info'),
    /**
     * Presence policy per protocol profile key: a device connection with no received byte for this many seconds is
     * closed as `idle_timeout` (the device becomes OFFLINE). Additive (gateway/v1 minor): gateways that ignore it keep
     * their local idle timeout. Configured in Central (Settings → Communication protocols).
     */
    offline_after_s: z.record(z.string().max(64), z.number().int().min(30).max(86_400)).optional(),
    extra: JsonObject.default({}),
});
export const CentralToGateway = {
    ack: AckBody,
    'gateway.welcome': z.object({
        session_id: Uuid,
        central_version: z.string().max(64),
        contract: z.literal(GATEWAY_CONTRACT),
        resume_from_seq: z.number().int().nonnegative(),
        max_unacked: z.number().int().min(1).max(100_000),
        config: GatewayConfig,
    }),
    'gateway.config': GatewayConfig,
    'command.dispatch': z.object({
        command_id: Uuid,
        idempotency_key: z.string().min(1).max(128),
        device_ref: DeviceRef,
        conn_ref_hint: ConnRef.optional(),
        capability: CapabilityKey,
        channel: z.number().int().nonnegative().optional(),
        params: JsonObject,
        deadline_at: IsoTimestamp,
    }),
    'command.cancel': z.object({ command_id: Uuid }),
    'device.query': z.object({
        query_id: Uuid,
        device_ref: DeviceRef,
        query_key: z.string().min(1).max(96),
        params: JsonObject,
    }),
    'connections.snapshot.request': z.object({}),
    'link.error': z.object({
        code: z.enum([
            'UNAUTHORIZED',
            'UNSUPPORTED_CONTRACT',
            'PROTOCOL_VIOLATION',
            'BACKPRESSURE',
            'SUPERSEDED',
        ]),
        detail: z.string().max(512).optional(),
    }),
};
/**
 * Types that are not sequenced/acknowledged: seq = 0. The handshake (hello/welcome) belongs to one link,
 * not to the per-boot message stream; ack and link.error are flow control / terminal errors.
 */
export const UNSEQUENCED_TYPES = new Set([
    'ack',
    'link.error',
    'gateway.hello',
    'gateway.welcome',
]);
/**
 * Validates an incoming text frame against the envelope and the body schema of its type.
 * Unknown types are reported (and must be ignored + counted by the caller) for additive compatibility.
 */
export function parseMessage(schemas, raw) {
    let json;
    try {
        json = JSON.parse(raw);
    }
    catch {
        return { kind: 'invalid', error: 'invalid_json' };
    }
    const env = Envelope.safeParse(json);
    if (!env.success)
        return { kind: 'invalid', error: 'invalid_envelope: ' + env.error.issues[0]?.path.join('.') };
    const envelope = env.data;
    const isUnsequenced = UNSEQUENCED_TYPES.has(envelope.type);
    if (!isUnsequenced && envelope.seq < 1)
        return { kind: 'invalid', error: 'invalid_seq' };
    const schema = schemas[envelope.type];
    if (!schema)
        return { kind: 'unknown_type', envelope };
    const body = schema.safeParse(envelope.body);
    if (!body.success) {
        const issue = body.error.issues[0];
        return {
            kind: 'invalid',
            error: `invalid_body(${envelope.type}): ${issue?.path.join('.')} ${issue?.message}`,
        };
    }
    return { kind: 'ok', envelope, type: envelope.type, body: body.data };
}
export const parseGatewayToCentral = (raw) => parseMessage(GatewayToCentral, raw);
export const parseCentralToGateway = (raw) => parseMessage(CentralToGateway, raw);
// ---------------------------------------------------------------------------------------------
// Device position (U12 auto-adoption). No new message types: Central asks with `device.query`
// (query_key LOCATION_QUERY) and the gateway answers with `device.query_result` whose `result` is a
// LocationResult. A gateway may also report a position spontaneously as `device.state`
// with `reported.location` = LocationResult (e.g. a periodic GPS report).
// ---------------------------------------------------------------------------------------------
export const LOCATION_QUERY = 'location.read';
export const LocationResult = z.object({
    lon: z.number().min(-180).max(180),
    lat: z.number().min(-90).max(90),
    /** Time of the fix (device clock may be wrong; Central keeps its own receive time as well). */
    fix_at: IsoTimestamp.optional(),
    /** Accuracy estimate in metres, if the device provides one. */
    accuracy_m: z.number().nonnegative().optional(),
});
//# sourceMappingURL=index.js.map